import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import { DataSource } from '../models/types';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page">
      <h1>Data sources</h1>
      <p>Named external DB lookups. Connection strings live in .env (e.g. DS_BILLING_URL).</p>

      <div class="card" *ngIf="isAdmin()">
        <h3>Add data source</h3>
        <input [(ngModel)]="name" placeholder="Name (billing)" />
        <select [(ngModel)]="dbType">
          <option value="mssql">MSSQL</option>
          <option value="mysql">MySQL</option>
        </select>
        <input [(ngModel)]="envKey" placeholder="Env key e.g. DS_BILLING_URL" />
        <textarea rows="4" [(ngModel)]="query" placeholder="SELECT ... WHERE phone = :phone"></textarea>
        <button (click)="create()">Create</button>
        <p class="err" *ngIf="error()">{{ error() }}</p>
      </div>

      <div class="card" *ngFor="let d of items()">
        <strong>{{ d.name }}</strong>
        <div class="meta">{{ d.db_type }} · {{ d.env_connection_key }}</div>
        <pre>{{ d.query_template }}</pre>
      </div>
    </div>
  `,
  styles: [
    `
      .page {
        display: grid;
        gap: 16px;
      }
      h1,
      h3 {
        margin: 0;
        font-family: Georgia, serif;
      }
      .card {
        background: white;
        border-radius: 16px;
        padding: 16px;
        display: grid;
        gap: 8px;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
      }
      input,
      select,
      textarea {
        border: 1px solid #cbd5e1;
        border-radius: 10px;
        padding: 10px;
      }
      button {
        border: 0;
        background: #0f766e;
        color: white;
        border-radius: 10px;
        padding: 10px 14px;
        width: fit-content;
        cursor: pointer;
      }
      .meta {
        color: #64748b;
      }
      pre {
        white-space: pre-wrap;
        background: #f8fafc;
        padding: 10px;
        border-radius: 8px;
      }
      .err {
        color: #b91c1c;
      }
    `,
  ],
})
export class DataSourcesPage implements OnInit {
  items = signal<DataSource[]>([]);
  name = '';
  dbType = 'mssql';
  envKey = 'DS_BILLING_URL';
  query = 'SELECT * FROM customers WHERE phone = :phone';
  error = signal('');

  constructor(private readonly api: ApiService, private readonly auth: AuthService) {}

  isAdmin(): boolean {
    return this.auth.user()?.role === 'admin';
  }

  async ngOnInit(): Promise<void> {
    this.items.set(await this.api.listDataSources());
  }

  async create(): Promise<void> {
    this.error.set('');
    try {
      const created = await this.api.createDataSource({
        name: this.name,
        db_type: this.dbType,
        env_connection_key: this.envKey,
        query_template: this.query,
        input_map_json: { phone: 'phone' },
        output_map_json: {},
        description: '',
      } as Partial<DataSource>);
      this.items.set([created, ...this.items()]);
      this.name = '';
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Create failed');
    }
  }
}
